/**
 * Adding, renaming and removing the shop's job titles.
 *
 * These were a fixed five — Owner, Manager, Cashier, Baker, Rider — so hiring
 * anyone whose job was not on that list meant filing them under the nearest
 * wrong one. Reached from the employee editor and from the Team tab, because
 * both are where you are standing when you find the title you want is missing.
 *
 * Positions are not permissions. What somebody may do in the app is carried by
 * their login, so naming a new one grants nothing — which is why this sits
 * with the team rather than behind Settings.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';

import { api } from '../lib/api.js';
import { Button, Input, Modal, Skeleton, cx, useToast } from '../components/ui.js';

export function PositionsModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const roles = useQuery({ queryKey: ['roles'], queryFn: api.staff.roles });

  /*
   * Fetched here rather than handed in. "How many people hold this" has to be
   * counted against the whole team, and this opens from inside an employee's
   * card as well as from the Team tab — where the only list to hand is that
   * one person, which would report every other position as held by nobody.
   */
  const employees = useQuery({
    queryKey: ['employees', 'all'],
    queryFn: () => api.staff.employees(true),
  });

  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  const counts = useMemo(() => {
    const tally = new Map<string, number>();
    for (const employee of employees.data ?? []) tally.set(employee.roleId, (tally.get(employee.roleId) ?? 0) + 1);
    return tally;
  }, [employees.data]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['roles'] });
    queryClient.invalidateQueries({ queryKey: ['employees'] });
  };

  const create = useMutation({
    mutationFn: () => api.staff.createRole({ name: newName.trim() }),
    onSuccess: (created) => {
      setNewName('');
      refresh();
      toast.push({ tone: 'ok', title: `${created.name} added` });
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not add it', body: error.message }),
  });

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => api.staff.updateRole(id, { name }),
    onSuccess: () => {
      setEditingId(null);
      refresh();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not rename it', body: error.message }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.staff.deleteRole(id),
    onSuccess: () => {
      refresh();
      toast.push({ tone: 'ok', title: 'Position removed' });
    },
    // The server names whoever still holds it, which is the next thing to do.
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not remove it', body: error.message }),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Positions"
      description="The job titles you can give someone. Renaming one changes it for everyone who holds it."
      footer={
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="flex gap-2">
          <Input
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && newName.trim()) create.mutate();
            }}
            placeholder="Administrator, Pastry chef, Barista…"
            className="min-w-0 flex-1"
            aria-label="New position name"
          />
          <Button
            variant="primary"
            icon={<Plus className="size-4" />}
            loading={create.isPending}
            disabled={!newName.trim()}
            onClick={() => create.mutate()}
          >
            Add
          </Button>
        </div>

        {roles.isLoading ? (
          <Skeleton className="h-40" />
        ) : (
          <ul className="divide-y divide-[var(--color-line)]">
            {roles.data?.map((role) => {
              const held = counts.get(role.id) ?? 0;
              const editing = editingId === role.id;

              return (
                <li key={role.id} className="flex items-center gap-2 py-2">
                  {editing ? (
                    <>
                      <Input
                        value={editingName}
                        onChange={(event) => setEditingName(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' && editingName.trim()) {
                            rename.mutate({ id: role.id, name: editingName });
                          }
                          if (event.key === 'Escape') setEditingId(null);
                        }}
                        className="min-w-0 flex-1"
                        aria-label={`Rename ${role.name}`}
                        autoFocus
                      />
                      <Button
                        variant="ghost"
                        className="shrink-0 px-2.5 text-[var(--color-ok)]"
                        aria-label="Save the new name"
                        disabled={!editingName.trim()}
                        onClick={() => rename.mutate({ id: role.id, name: editingName })}
                        icon={<Check className="size-4" />}
                      />
                      <Button
                        variant="ghost"
                        className="shrink-0 px-2.5"
                        aria-label="Cancel renaming"
                        onClick={() => setEditingId(null)}
                        icon={<X className="size-4" />}
                      />
                    </>
                  ) : (
                    <>
                      <span className="min-w-0 flex-1 truncate text-sm font-medium">{role.name}</span>
                      <span className="shrink-0 text-xs text-[var(--color-ink-faint)]">
                        {held === 0 ? 'nobody' : held === 1 ? '1 person' : `${held} people`}
                      </span>
                      <Button
                        variant="ghost"
                        className="shrink-0 px-2.5"
                        aria-label={`Rename ${role.name}`}
                        onClick={() => {
                          setEditingId(role.id);
                          setEditingName(role.name);
                        }}
                        icon={<Pencil className="size-4" />}
                      />
                      {/*
                        * The owner's position is part of the shop. Everything
                        * else can go, once nobody is standing on it.
                        */}
                      <Button
                        variant="ghost"
                        className={cx(
                          'shrink-0 px-2.5',
                          role.isProtected || held > 0
                            ? 'opacity-30'
                            : 'text-[var(--color-danger)] hover:bg-[var(--color-danger)]/10',
                        )}
                        aria-label={`Remove ${role.name}`}
                        title={
                          role.isProtected
                            ? 'This one is part of the shop'
                            : held > 0
                              ? 'Somebody still holds this position'
                              : 'Remove this position'
                        }
                        disabled={role.isProtected || held > 0}
                        onClick={() => remove.mutate(role.id)}
                        icon={<Trash2 className="size-4" />}
                      />
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
}
